import React, { useEffect, useRef, useState } from "react";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaImage,
  FaTimes,
  FaSave,
  FaSyncAlt,
} from "react-icons/fa";

import "./Team.css";
import { BASE_URL } from "../../Config/config";
export default function Team() {
  /* =========================================================
     CONFIG
  ========================================================= */

  /* =========================================================
     REFS
  ========================================================= */

  const fileInputRef = useRef(null);

  /* =========================================================
     STATES
  ========================================================= */

  const [teams, setTeams] = useState([]);

  const [name, setName] = useState("");

  const [designation, setDesignation] = useState("");

  const [selectedImage, setSelectedImage] = useState(null);

  const [preview, setPreview] = useState("");

  const [editingId, setEditingId] = useState(null);

  const [loading, setLoading] = useState(false);

  const [fetching, setFetching] = useState(false);

  /* =========================================================
     IMAGE URL HELPER
  ========================================================= */

  const getImageUrl = (image) => {
    if (!image) {
      return "";
    }

    if (image.startsWith("http://") || image.startsWith("https://")) {
      return image;
    }

    return `${BASE_URL}/${image.replace(/^\/+/, "")}`;
  };

  /* =========================================================
     FETCH TEAM MEMBERS
  ========================================================= */

  const fetchTeams = async () => {
    try {
      setFetching(true);

      const response = await fetch(`${BASE_URL}/team.php?action=get`);

      const text = await response.text();

      let result;

      try {
        result = JSON.parse(text);
      } catch (jsonError) {
        console.error("Invalid JSON returned by server:", text);

        alert("Server returned an invalid response. Please check team.php.");

        return;
      }

      if (result.status) {
        setTeams(Array.isArray(result.data) ? result.data : []);
      } else {
        console.error(result.message || "Failed to fetch team members.");
      }
    } catch (error) {
      console.error("Fetch team members error:", error);
    } finally {
      setFetching(false);
    }
  };

  /* =========================================================
     INITIAL LOAD
  ========================================================= */

  useEffect(() => {
    fetchTeams();
  }, []);

  /* =========================================================
     SELECT IMAGE
  ========================================================= */

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    /* ---------------------------------------------------------
       FILE TYPE
    --------------------------------------------------------- */

    if (!file.type.startsWith("image/")) {
      alert("Please select a valid image.");

      e.target.value = "";

      return;
    }

    /* ---------------------------------------------------------
       FILE SIZE
    --------------------------------------------------------- */

    if (file.size > 5 * 1024 * 1024) {
      alert("Image size must be less than 5 MB.");

      e.target.value = "";

      return;
    }

    /* ---------------------------------------------------------
       SAVE FILE
    --------------------------------------------------------- */

    setSelectedImage(file);

    /* ---------------------------------------------------------
       CREATE PREVIEW
    --------------------------------------------------------- */

    const imageUrl = URL.createObjectURL(file);

    setPreview(imageUrl);
  };

  /* =========================================================
     SUBMIT
  ========================================================= */

  const handleSubmit = async (e) => {
    e.preventDefault();

    /* ---------------------------------------------------------
       VALIDATION
    --------------------------------------------------------- */

    if (!name.trim()) {
      alert("Please enter team member name.");

      return;
    }

    if (!designation.trim()) {
      alert("Please enter designation.");

      return;
    }

    if (!selectedImage && !editingId) {
      alert("Please select team photo.");

      return;
    }

    try {
      setLoading(true);

      /* -------------------------------------------------------
         FORM DATA
      ------------------------------------------------------- */

      const formData = new FormData();

      formData.append("name", name.trim());

      formData.append("designation", designation.trim());

      if (selectedImage) {
        formData.append("image", selectedImage);
      }

      if (editingId) {
        formData.append("id", editingId);
      }

      /* -------------------------------------------------------
         ACTION
      ------------------------------------------------------- */

      const action = editingId ? "update" : "add";

      /* -------------------------------------------------------
         API REQUEST
      ------------------------------------------------------- */

      const response = await fetch(`${BASE_URL}/team.php?action=${action}`, {
        method: "POST",
        body: formData,
      });

      const text = await response.text();

      let result;

      try {
        result = JSON.parse(text);
      } catch (jsonError) {
        console.error("Invalid JSON returned from server:", text);

        alert("Server returned an invalid response. Please check team.php.");

        return;
      }

      /* -------------------------------------------------------
         SUCCESS
      ------------------------------------------------------- */

      if (result.status) {
        alert(
          editingId
            ? "Team member updated successfully."
            : "Team member added successfully.",
        );

        resetForm();

        await fetchTeams();
      } else {
        alert(result.message || "Something went wrong.");
      }
    } catch (error) {
      console.error("Save team member error:", error);

      alert("Unable to connect to server.");
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     EDIT TEAM MEMBER
  ========================================================= */

  const handleEdit = (team) => {
    setEditingId(team.id);

    setName(team.name || "");

    setDesignation(team.designation || "");

    setSelectedImage(null);

    setPreview(getImageUrl(team.image));

    /* ---------------------------------------------------------
       RESET FILE INPUT
    --------------------------------------------------------- */

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    /* ---------------------------------------------------------
       SCROLL TO FORM
    --------------------------------------------------------- */

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /* =========================================================
     DELETE TEAM MEMBER
  ========================================================= */

  const handleDelete = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this team member?",
    );

    if (!confirmed) {
      return;
    }

    try {
      setLoading(true);

      const response = await fetch(
        `${BASE_URL}/team.php?action=delete&id=${id}`,
        {
          method: "DELETE",
        },
      );

      const text = await response.text();

      let result;

      try {
        result = JSON.parse(text);
      } catch (jsonError) {
        console.error("Invalid JSON returned from server:", text);

        alert("Server returned an invalid response.");

        return;
      }

      if (result.status) {
        alert("Team member deleted successfully.");

        /* -----------------------------------------------------
           If deleted item was being edited
        ----------------------------------------------------- */

        if (String(editingId) === String(id)) {
          resetForm();
        }

        await fetchTeams();
      } else {
        alert(result.message || "Delete failed.");
      }
    } catch (error) {
      console.error("Delete team member error:", error);

      alert("Unable to connect to server.");
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     RESET FORM
  ========================================================= */

  const resetForm = () => {
    setEditingId(null);

    setName("");

    setDesignation("");

    setSelectedImage(null);

    setPreview("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /* =========================================================
     REMOVE PREVIEW
  ========================================================= */

  const removePreview = () => {
    setSelectedImage(null);

    setPreview("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /* =========================================================
     OPEN FILE PICKER
  ========================================================= */

  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div className="team-admin-page">
      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

      <div className="team-page-header">
        <div>
          <h1>Team Photos</h1>

          <p>Manage your team members and their photos.</p>
        </div>

        <div className="team-count">
          <FaImage />

          <span>
            {teams.length} {teams.length === 1 ? "Member" : "Members"}
          </span>
        </div>
      </div>

      {/* =====================================================
          FORM CARD
      ===================================================== */}

      <div className="team-form-card">
        {/* ===================================================
            FORM HEADER
        =================================================== */}

        <div className="team-form-header">
          <div>
            <h2>{editingId ? "Edit Team Member" : "Add Team Member"}</h2>

            <p>
              {editingId
                ? "Update the team member information and photo."
                : "Add a new team member with their photo."}
            </p>
          </div>

          {editingId && (
            <button
              type="button"
              className="team-cancel-btn"
              onClick={resetForm}
              disabled={loading}
            >
              <FaTimes />
              Cancel Edit
            </button>
          )}
        </div>

        {/* ===================================================
            FORM
        =================================================== */}

        <form onSubmit={handleSubmit}>
          {/* =================================================
              NAME + DESIGNATION
          ================================================= */}

          <div className="team-form-grid">
            {/* NAME */}

            <div className="team-form-group">
              <label>
                Team Member Name
                <span>*</span>
              </label>

              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter team member name"
                maxLength={150}
                disabled={loading}
              />
            </div>

            {/* DESIGNATION */}

            <div className="team-form-group">
              <label>
                Designation
                <span>*</span>
              </label>

              <input
                type="text"
                value={designation}
                onChange={(e) => setDesignation(e.target.value)}
                placeholder="Enter designation"
                maxLength={150}
                disabled={loading}
              />
            </div>
          </div>

          {/* =================================================
              IMAGE UPLOAD
          ================================================= */}

          <div className="team-upload-area">
            {preview ? (
              <div className="team-preview-wrapper">
                <img
                  src={preview}
                  alt="Team preview"
                  className="team-preview"
                />

                {/* REMOVE */}

                <button
                  type="button"
                  className="remove-preview"
                  onClick={removePreview}
                  disabled={loading}
                  title="Remove image"
                >
                  <FaTimes />
                </button>
              </div>
            ) : (
              <div
                className="team-upload-box"
                onClick={openFilePicker}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    openFilePicker();
                  }
                }}
              >
                <FaImage />

                <h3>Upload Team Photo</h3>

                <p>Click here to select an image</p>

                <span>JPG, JPEG, PNG or WEBP • Max 5 MB</span>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleImageChange}
              hidden
            />
          </div>

          {/* =================================================
              CHANGE IMAGE
          ================================================= */}

          {preview && (
            <button
              type="button"
              className="change-image-btn"
              onClick={openFilePicker}
              disabled={loading}
            >
              <FaEdit />
              Change Image
            </button>
          )}

          {/* =================================================
              FORM ACTIONS
          ================================================= */}

          <div className="team-form-actions">
            {/* RESET */}

            <button
              type="button"
              className="team-reset-btn"
              onClick={resetForm}
              disabled={loading}
            >
              <FaSyncAlt />
              Reset
            </button>

            {/* SAVE */}

            <button type="submit" className="team-save-btn" disabled={loading}>
              {loading ? (
                <>
                  <FaSyncAlt className="team-loading-icon" />

                  {editingId ? "Updating..." : "Saving..."}
                </>
              ) : (
                <>
                  {editingId ? <FaSave /> : <FaPlus />}

                  {editingId ? "Update Team Member" : "Add Team Member"}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* =====================================================
          TEAM GALLERY
      ===================================================== */}

      <div className="team-list-section">
        {/* ===================================================
            LIST HEADER
        =================================================== */}

        <div className="team-list-header">
          <div>
            <h2>Team Gallery</h2>

            <p>All team members uploaded to the website.</p>
          </div>
        </div>

        {/* ===================================================
            LOADING
        =================================================== */}

        {fetching ? (
          <div className="team-empty">
            <FaSyncAlt className="team-loading-icon" />

            <h3>Loading Team Members</h3>

            <p>Please wait while we load the team gallery.</p>
          </div>
        ) : teams.length === 0 ? (
          /* =================================================
             EMPTY
          ================================================= */

          <div className="team-empty">
            <FaImage />

            <h3>No Team Members</h3>

            <p>Add your first team member using the form above.</p>
          </div>
        ) : (
          /* =================================================
             TEAM GRID
          ================================================= */

          <div className="team-grid">
            {teams.map((team) => (
              <div className="team-admin-card" key={team.id}>
                {/* =========================================
                    IMAGE
                ========================================= */}

                <div className="team-admin-image">
                  <img
                    src={getImageUrl(team.image)}
                    alt={team.name || "Team member"}
                    loading="lazy"
                  />

                  {/* =======================================
                      OVERLAY
                  ======================================= */}

                  <div className="team-admin-overlay">
                    {/* EDIT */}

                    <button
                      type="button"
                      onClick={() => handleEdit(team)}
                      disabled={loading}
                      title="Edit team member"
                    >
                      <FaEdit />
                    </button>

                    {/* DELETE */}

                    <button
                      type="button"
                      onClick={() => handleDelete(team.id)}
                      disabled={loading}
                      title="Delete team member"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>

                {/* =========================================
                    INFORMATION
                ========================================= */}

                <div className="team-admin-info">
                  <h3 title={team.name}>{team.name}</h3>

                  <p title={team.designation}>{team.designation}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
